import {useEffect, useRef, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Home,
  Info,
  Loader2,
  Printer,
  RotateCw,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@common-web/components/ui/accordion';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {formatUsd, isCheckoutValid} from '@client/pages/public/enroll/enrollModel';
import type {Applicant} from '@client/pages/public/enroll/enrollModel';
import {groupRequiresAccessCode, type GroupConfig} from '@client/pages/public/enroll/types';
import {
  BENEFICIARY_RELATIONSHIP_OPTIONS,
  RELATIONSHIP_OPTIONS,
  GENDER_OPTIONS,
} from '@client/pages/public/enroll/checkout/checkoutSchema';
import {renderRichText} from '@client/pages/public/enroll/richText';
import {AppDownloadBanner} from '@client/pages/public/enroll/components/appDownload';
import {
  AccessExpiredError,
  AlreadyEnrolledError,
  buildEnrollmentSubmission,
  submitEnrollment,
} from '@client/pages/public/enroll/confirmation/enrollmentApi';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

const GENDER_LABELS: Record<string, string> = Object.fromEntries(
  GENDER_OPTIONS.map((option) => [option.value, option.label]),
);

const RELATIONSHIP_LABELS: Record<string, string> = Object.fromEntries(
  [...RELATIONSHIP_OPTIONS, ...BENEFICIARY_RELATIONSHIP_OPTIONS].map((option) => [
    option.value,
    option.label,
  ]),
);

/** Masked SSN with oversized dots so the redaction reads clearly, e.g. ●●●-●●-1234. */
function MaskedSsn({ssn}: {ssn: string}) {
  const last4 = ssn.replace(/\D/g, '').slice(-4);
  return (
    <span className='whitespace-nowrap'>
      <span className='align-middle text-lg leading-none tracking-[0.12em]'>
        •••-••-{last4 ? '' : '••••'}
      </span>
      {last4}
    </span>
  );
}

function formatDob(dob: string): string {
  const [year, month, day] = dob.split('-');
  if (!year || !month || !day) return dob;
  return `${Number(month)}/${Number(day)}/${year}`;
}

function formatAddress(info: Applicant): string {
  const street = [info.address1, info.address2].filter(Boolean).join(' ');
  return `${street}, ${info.city}, ${info.state} ${info.zip}`;
}

const submittedFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * Post-submission receipt. On arrival it runs the submission and shows a loading state with
 * ghost elements; once the confirmation resolves it renders the receipt — no progress bar, no cart.
 * If there's no confirmation and no completed checkout (direct nav / cleared session), it bounces
 * back to the overview rather than submitting empty data.
 */
export function ConfirmationStep() {
  const {
    group,
    enrollmentReceipt,
    applicant,
    dependents,
    beneficiary,
    beneficiaryRequired,
    tiers,
    selections,
    setEnrollmentReceipt,
    reset,
  } = useEnrollSteps();
  const payPeriod = sitePayPeriod(group);
  const {isPreview, isDemo} = useEnrollPreview();
  const {track} = useEnrollAnalytics();
  const navigate = useNavigate();
  const base = `/${group.slug}`;

  // Clear the whole enrollment (selections, applicant, dependents, receipt) on the way home.
  function handleBackToHome() {
    reset();
    navigate(base);
  }

  // Keeps their entered data (state lives in context) — only a hard refresh would wipe it.
  function handleBackToCheckout() {
    navigate(`${base}/checkout`);
  }

  // Only submit if the user actually finished checkout (applicant, dependents, beneficiary valid).
  // Existing receipt = already submitted.
  const canSubmit = isCheckoutValid(group, {selections, applicant, dependents, beneficiary});
  const [error, setError] = useState(false);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (enrollmentReceipt || !canSubmit || startedRef.current) return;
    startedRef.current = true;
    setError(false);
    const submission = buildEnrollmentSubmission(
      group,
      tiers,
      selections,
      applicant,
      dependents,
      beneficiaryRequired ? beneficiary : null,
    );
    submitEnrollment(submission, {preview: isPreview, demo: isDemo, requiresAccessCode: groupRequiresAccessCode(group)})
      .then((result) => {
        track(EnrollEvent.SubmissionSucceeded, {
          plan_count: Object.values(selections).filter((selection) => selection?.planId).length,
          declined_count: Object.values(selections).filter((selection) => selection?.declined)
            .length,
          dependent_count: dependents.length,
        });
        setEnrollmentReceipt(result);
      })
      .catch((err) => {
        if (err instanceof AlreadyEnrolledError) {
          track(EnrollEvent.SubmissionFailed, {reason: 'already_enrolled'});
          setAlreadyEnrolled(true);
        }
        // Missing access code -> retry can't help; send them back to the gate to re-enter it.
        else if (err instanceof AccessExpiredError) {
          track(EnrollEvent.SubmissionFailed, {reason: 'access_expired'});
          navigate(`${base}/access-code`, {replace: true});
        } else {
          track(EnrollEvent.SubmissionFailed, {reason: 'error'});
          setError(true);
          startedRef.current = false; // let the user retry in place, no full-page refresh
        }
      });
  }, [
    attempt,
    enrollmentReceipt,
    canSubmit,
    group,
    tiers,
    selections,
    applicant,
    dependents,
    beneficiary,
    beneficiaryRequired,
    setEnrollmentReceipt,
    track,
    navigate,
    base,
    isPreview,
  ]);

  if (!enrollmentReceipt && !canSubmit) {
    return <Navigate to={base} replace />;
  }

  if (alreadyEnrolled) {
    return <AlreadyEnrolled group={group} onHome={handleBackToHome} />;
  }

  if (!enrollmentReceipt) {
    return (
      <LoadingReceipt
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        onBackToCheckout={handleBackToCheckout}
      />
    );
  }

  const {
    subscriber,
    dependents: enrolled,
    beneficiary: enrolledBeneficiary,
    elections,
    totalWeekly,
    confirmationNumber,
    submittedAt,
  } = enrollmentReceipt;
  const {whatHappensNext, faq, effectiveDateNote} = group.confirmation;
  // Beneficiary contact fields are optional, so show only the parts entered.
  const beneficiaryAddress = enrolledBeneficiary
    ? [
        enrolledBeneficiary.address,
        enrolledBeneficiary.city,
        `${enrolledBeneficiary.state} ${enrolledBeneficiary.zip}`.trim(),
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  // No priced lines means every coverage was declined/skipped — show the declination variant.
  const declinedEverything = elections.length === 0;

  return (
    <div className='mx-auto max-w-5xl space-y-6 pb-4'>
      {isPreview && (
        <div className='rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          {isDemo
            ? 'Demo site — this is a sample confirmation. No enrollment was submitted.'
            : 'Sample confirmation — nothing was submitted. This is how the receipt will look.'}
        </div>
      )}
      <section className='rounded-2xl border border-neutral-200 bg-white p-8 text-center'>
        <CheckCircle2 className='mx-auto h-12 w-12 text-green-500' />
        <h1 className={`mt-4 text-3xl font-bold text-[var(--enroll-ink)] ${POSTHOG_UNMASK_CLASS}`}>
          {declinedEverything ? 'Your declination is recorded.' : "You're enrolled."}
        </h1>
        {declinedEverything ? (
          <p className='mt-3 text-neutral-600'>
            You have declined coverage for the 2026 plan year.
          </p>
        ) : effectiveDateNote ? (
          <p className='mt-3 font-semibold text-[var(--enroll-ink)]'>{effectiveDateNote}</p>
        ) : (
          <p className='mt-3 text-neutral-600'>
            Your {group.benefitsContact ?? 'HR office'} will confirm your coverage effective date.
          </p>
        )}
        <p className='mt-1 text-neutral-600'>
          A confirmation email is on its way to{' '}
          <span className='ph-no-capture font-medium text-neutral-900'>{subscriber.email}</span>.
        </p>
        <div className='mt-5 flex flex-col items-center gap-1.5'>
          {confirmationNumber && (
            <span className='inline-flex items-center rounded-full bg-[color-mix(in_srgb,var(--enroll-accent)_12%,white)] px-3 py-1 text-sm font-semibold text-[var(--enroll-accent)]'>
              Confirmation #{confirmationNumber}
            </span>
          )}
          <p className={`text-xs text-neutral-400 ${POSTHOG_UNMASK_CLASS}`}>
            Submitted {submittedFormatter.format(new Date(submittedAt))}
          </p>
        </div>
      </section>

      {!declinedEverything && <AppDownloadBanner />}

      <section className={`rounded-2xl border border-neutral-200 bg-white p-6 ${POSTHOG_UNMASK_CLASS}`}>
        <h2 className='text-xl font-semibold text-[var(--enroll-ink)]'>Your elections</h2>
        {declinedEverything ? (
          <p className='mt-4 text-sm leading-relaxed text-neutral-500'>
            You declined all available coverage for the 2026 plan year. You can enroll again at the
            next open enrollment period, or sooner if you have a qualifying life event.
          </p>
        ) : (
          <ul className='mt-4 divide-y divide-neutral-100'>
            {group.products.map((product) => {
              const election = elections.find((e) => e.category === product.category);
              if (election) {
                return (
                  <li
                    key={product.category}
                    className='flex items-start justify-between gap-4 py-3 first:pt-0'
                  >
                    <div className='min-w-0'>
                      <p className='font-semibold text-neutral-900'>{election.planName}</p>
                      <p className='text-sm text-neutral-500'>
                        {[election.tierLabel, election.network].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className='whitespace-nowrap font-semibold text-neutral-900'>
                      {formatUsd(election.weeklyCost)}
                      <span className='text-xs font-normal text-neutral-400'>{payPeriod.priceSuffix}</span>
                    </span>
                  </li>
                );
              }
              // Show an explicit "Declined" line only for declinable coverage (e.g. medical),
              // not categories the user simply skipped (dental/vision).
              if (product.declineStyle !== 'skipped' && selections[product.category]?.declined) {
                return (
                  <li
                    key={product.category}
                    className='flex items-start justify-between gap-4 py-3 first:pt-0'
                  >
                    <div className='min-w-0'>
                      <p className='font-semibold text-neutral-900'>{product.label}</p>
                      <p className='text-sm text-neutral-500'>Coverage declined</p>
                    </div>
                    <span className='whitespace-nowrap text-sm font-semibold text-neutral-900'>
                      Declined
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        )}
        {!declinedEverything && (
          <div className='mt-4 flex items-center justify-between border-t border-neutral-200 pt-4'>
            <span className='text-sm font-semibold text-neutral-700'>{payPeriod.total}</span>
            <span className='text-2xl font-bold text-[var(--enroll-ink)]'>
              {formatUsd(totalWeekly)}
            </span>
          </div>
        )}
      </section>

      <div className='grid gap-4 sm:grid-cols-2'>
        <section className='ph-no-capture rounded-2xl border border-neutral-200 bg-white p-6'>
          <h3 className='text-sm font-semibold text-neutral-900'>Subscriber</h3>
          <div className='mt-3 space-y-1 text-sm text-neutral-600'>
            <p className='font-medium text-neutral-900'>
              {subscriber.firstName} {subscriber.lastName}
            </p>
            <p>
              DOB {formatDob(subscriber.dob)}
              {GENDER_LABELS[subscriber.gender] && <> · {GENDER_LABELS[subscriber.gender]}</>}
            </p>
            <p>
              SSN <MaskedSsn ssn={subscriber.ssn} />
            </p>
            <p>{formatAddress(subscriber)}</p>
          </div>
        </section>

        <section className='ph-no-capture rounded-2xl border border-neutral-200 bg-white p-6'>
          <h3 className='text-sm font-semibold text-neutral-900'>Dependents ({enrolled.length})</h3>
          {enrolled.length === 0 ? (
            <p className='mt-3 text-sm text-neutral-500'>None added.</p>
          ) : (
            <ul className='mt-3 space-y-2 text-sm text-neutral-600'>
              {enrolled.map((dependent, index) => (
                <li key={`${dependent.firstName}-${dependent.lastName}-${index}`}>
                  <span className='font-medium text-neutral-900'>
                    {dependent.firstName} {dependent.lastName}
                  </span>{' '}
                  · {RELATIONSHIP_LABELS[dependent.relationship] ?? dependent.relationship}
                </li>
              ))}
            </ul>
          )}
        </section>

        {enrolledBeneficiary && (
          <section className='ph-no-capture rounded-2xl border border-neutral-200 bg-white p-6'>
            <h3 className='text-sm font-semibold text-neutral-900'>Beneficiary</h3>
            <div className='mt-3 space-y-1 text-sm text-neutral-600'>
              <p>
                <span className='font-medium text-neutral-900'>
                  {enrolledBeneficiary.firstName} {enrolledBeneficiary.lastName}
                </span>{' '}
                ·{' '}
                {RELATIONSHIP_LABELS[enrolledBeneficiary.relationship] ??
                  enrolledBeneficiary.relationship}
              </p>
              {enrolledBeneficiary.dob && <p>DOB {formatDob(enrolledBeneficiary.dob)}</p>}
              {beneficiaryAddress && <p>{beneficiaryAddress}</p>}
            </div>
          </section>
        )}
      </div>

      <section className={`rounded-2xl border border-neutral-200 bg-white p-6 ${POSTHOG_UNMASK_CLASS}`}>
        <h2 className='text-xl font-semibold text-[var(--enroll-ink)]'>What happens next</h2>
        <p className='mt-1 text-sm text-neutral-500'>
          A few things to expect now that your elections are in. For anything specific to your
          situation, check with your {group.benefitsContact ?? 'HR department'}.
        </p>
        <ol className='mt-5 space-y-5'>
          {whatHappensNext.map((step, index) => (
            <li key={step.title} className='flex gap-4'>
              <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--enroll-primary)_12%,white)] text-sm font-semibold text-[var(--enroll-primary)]'>
                {index + 1}
              </span>
              <div>
                <p className='font-semibold text-neutral-900'>{step.title}</p>
                <p className='mt-0.5 text-sm leading-relaxed text-neutral-600'>
                  {renderRichText(step.body)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className={`rounded-2xl border border-neutral-200 bg-white p-6 ${POSTHOG_UNMASK_CLASS}`}>
        <h2 className='text-xl font-semibold text-[var(--enroll-ink)]'>Common questions</h2>
        <Accordion type='multiple' className='mt-2'>
          {faq.map((item) => (
            <AccordionItem key={item.question} value={item.question} className='last:border-b-0'>
              <AccordionTrigger className='text-left text-base font-semibold text-neutral-900'>
                {item.question}
              </AccordionTrigger>
              <AccordionContent className='text-base leading-relaxed text-neutral-600'>
                {renderRichText(item.answer)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <p className='text-xs leading-relaxed text-neutral-400'>
        <span className='font-medium text-neutral-500'>Section 125 / Cafeteria Plan Notice.</span>{' '}
        Pre-tax benefit elections are generally irrevocable during the plan year unless a permitted
        election change event occurs under Section 125 of the Internal Revenue Code and the Plan's
        governing documents.
      </p>

      <div className='flex flex-col-reverse items-center justify-center gap-3 sm:flex-row'>
        <button
          type='button'
          onClick={() => window.print()}
          className='inline-flex h-11 items-center gap-2 rounded-lg border border-neutral-300 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100'
        >
          <Printer className='h-4 w-4' />
          Print confirmation
        </button>
        <button
          type='button'
          onClick={handleBackToHome}
          className='inline-flex h-11 items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white transition-colors'
          style={{backgroundColor: 'var(--enroll-primary)'}}
        >
          <Home className='h-4 w-4' />
          Back to home
        </button>
      </div>
    </div>
  );
}

function Ghost({className}: {className: string}) {
  return <div className={`animate-pulse rounded bg-neutral-200 ${className}`} />;
}

function GhostCard({rows}: {rows: number}) {
  return (
    <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
      <Ghost className='h-5 w-36' />
      <div className='mt-5 space-y-3'>
        {Array.from({length: rows}).map((_, index) => (
          <div key={index} className='flex items-center justify-between gap-4'>
            <Ghost className='h-4 w-2/3' />
            <Ghost className='h-4 w-12' />
          </div>
        ))}
      </div>
    </section>
  );
}

function AlreadyEnrolled({group, onHome}: {group: GroupConfig; onHome: () => void}) {
  const phoneDigits = group.supportPhone.replace(/\D/g, '');
  return (
    <div className='mx-auto max-w-2xl pb-4'>
      <section className='rounded-2xl border border-neutral-200 bg-white p-10 text-center'>
        <Info className='mx-auto h-12 w-12 text-[var(--enroll-primary)]' />
        <h1 className='mt-4 text-3xl font-bold text-[var(--enroll-ink)]'>
          You're already enrolled
        </h1>
        <p className='mt-3 text-neutral-600'>
          Our records show you already have coverage for the current plan year. To review or change
          your elections, contact the Concierge Team.
        </p>
        <div className='mt-5 flex flex-col items-center gap-1 text-sm'>
          <a href={`tel:${phoneDigits}`} className='font-semibold text-[var(--enroll-ink)]'>
            {group.supportPhone}
          </a>
          {group.supportEmail && (
            <a href={`mailto:${group.supportEmail}`} className='text-neutral-500'>
              {group.supportEmail}
            </a>
          )}
        </div>
        <button
          type='button'
          onClick={onHome}
          className='mt-6 inline-flex h-11 items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white'
          style={{backgroundColor: 'var(--enroll-primary)'}}
        >
          <Home className='h-4 w-4' />
          Back to home
        </button>
      </section>
    </div>
  );
}

function LoadingReceipt({
  error,
  onRetry,
  onBackToCheckout,
}: {
  error: boolean;
  onRetry: () => void;
  onBackToCheckout: () => void;
}) {
  return (
    <div className='mx-auto max-w-5xl space-y-6 pb-4'>
      <section className='rounded-2xl border border-neutral-200 bg-white p-10 text-center'>
        {error ? (
          <>
            <AlertCircle className='mx-auto h-10 w-10 text-red-500' />
            <p className='mt-4 text-lg font-semibold text-[var(--enroll-ink)]'>
              We couldn't submit your enrollment
            </p>
            <p className='mt-1 text-sm text-neutral-500'>
              Your information is saved. Try again, or go back to review your details.
            </p>
            <div className='mt-6 flex flex-col-reverse items-center justify-center gap-3 sm:flex-row'>
              <button
                type='button'
                onClick={onBackToCheckout}
                className='inline-flex h-11 items-center gap-2 rounded-lg border border-neutral-300 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100'
              >
                <ArrowLeft className='h-4 w-4' />
                Back to checkout
              </button>
              <button
                type='button'
                onClick={onRetry}
                className='inline-flex h-11 items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white transition-colors'
                style={{backgroundColor: 'var(--enroll-primary)'}}
              >
                <RotateCw className='h-4 w-4' />
                Try again
              </button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className='mx-auto h-10 w-10 animate-spin text-[var(--enroll-primary)]' />
            <p className='mt-4 text-lg font-semibold text-[var(--enroll-ink)]'>
              Submitting your enrollment…
            </p>
            <p className='mt-1 text-sm text-neutral-500'>
              Hang tight while we finalize your elections.
            </p>
          </>
        )}
      </section>

      {!error && (
        <>
          <GhostCard rows={3} />
          <div className='grid gap-4 sm:grid-cols-2'>
            <GhostCard rows={3} />
            <GhostCard rows={2} />
          </div>
        </>
      )}
    </div>
  );
}
